/* eslint-disable @typescript-eslint/no-misused-promises */
import { useTranslation } from 'react-i18next';
import { useFetchAiChatListQuery, useBindAiChatMutation, useRemoveAiChatMutation } from 'rtkQuery/sharespro/queries/chat';
import AppCard from 'presentation/components/shared/card/AppCard';
import { Spinner, Text, Center, Container, Box, VStack, IconButton, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay, Button, CloseButton } from '@chakra-ui/react';
import { FC, useRef, useState } from 'react';
import { AiChat } from 'model/modules/chatAi/chatAi';
import PageLayout from 'presentation/layout/shared/page/PageLayout';
import SidebarLayout from 'presentation/layout/shared/SidebarLayout';
import { useChat } from './ChatContext';
import { v4 as uuidv4 } from 'uuid';
import SidebarMenuItem from 'presentation/components/shared/sidebar/menu/SidebarMenuItem';
import { Color } from 'presentation/theme/colorScheme';

const AiChatSidebarList: FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { setActiveChat, activeChat, resetChat } = useChat();
  const [bindAiChat] = useBindAiChatMutation();
  const [removeAiChat] = useRemoveAiChatMutation();
  const { data: chats, isLoading, refetch } = useFetchAiChatListQuery();

  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const handleCreateChat = async (): Promise<void> => {
    const newChatId = uuidv4();
    try {
      resetChat();
      await bindAiChat({ externalId: newChatId }).unwrap();
      await refetch();
      const newChat: AiChat = {
        id: newChatId,
        createdAt: new Date(),
      };
      setActiveChat(newChat);
    } catch (error) {
      console.error('Ошибка при создании чата:', error);
    }
  };

  const handleDeleteChat = async (chatId: string): Promise<void> => {
    try {
      await removeAiChat({ externalId: chatId }).unwrap();
      if (activeChat?.id === chatId) {
        resetChat();
      }
      await refetch();
    } catch (error) {
      console.error('Ошибка при удалении чата:', error);
    }
  };

  if (isLoading) {
    return (
      <PageLayout>
        <Container display="flex" alignItems="flex-start">
            <Center py={4}><Spinner size="sm" /></Center>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container display="flex" alignItems="flex-start">
        <SidebarLayout>
          <Box w="230px">
              <SidebarMenuItem
                onClick={() => { void handleCreateChat(); }}
                text={t('createNewChat')}
                isActive={false}
                isLast={true}
                isFirst={true}
                color={Color.Primary500}
              />
              </Box>
               <Box h="10px" />
          <AppCard display="flex" flexDir="column" w="230px">
            <VStack spacing={0} align="stretch">
              {chats?.map((chat: AiChat) => (
                <Box
                  key={chat.id}
                  bg={activeChat?.id === chat.id ? 'gray.100' : 'transparent'}
                  _hover={{ bg: 'gray.50' }}
                  borderRadius="md"
                  px={3}
                  py={2}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  cursor="pointer"
                  onClick={() => setActiveChat(chat)}
                >
                  <Box
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    maxW="200px"
                  >
                    <Text overflow="hidden" textOverflow="ellipsis">{chat.lastMessage?.text.replace(/[#*_`>\\-]/g, '')
      .replace(/\\n/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()}</Text>
                  </Box>
                  <Box w="20px">
                  <IconButton
                    aria-label={t('delete')}
                    icon={<CloseButton size="sm" w="5px" h="10px"/>}
                    size="xs"
                    variant="ghost"
                    opacity={0.5}
                    color="gray.400"
                    onClick={e => {
                      e.stopPropagation();
                      setChatToDelete(chat.id);
                    }}
                  />
                  </Box>
                </Box>
              ))}
            </VStack>
          </AppCard>
        </SidebarLayout>
        <Box flex="1">{children}</Box>
        <AlertDialog
          isOpen={!!chatToDelete}
          leastDestructiveRef={cancelRef}
          onClose={() => setChatToDelete(null)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {t('confirmDeleteTitle', { defaultValue: 'Удалить чат?' })}
              </AlertDialogHeader>
              <AlertDialogBody>
                {t('confirmDeleteBody', { defaultValue: 'Вы уверены, что хотите удалить этот чат? Это действие необратимо.' })}
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setChatToDelete(null)}>
                  {t('cancel', { defaultValue: 'Отмена' })}
                </Button>
                <Button colorScheme="red" ml={3} onClick={async () => {
                  if (chatToDelete) {
                    await handleDeleteChat(chatToDelete);
                    setChatToDelete(null);
                  }
                }}>
                  {t('delete', { defaultValue: 'Удалить' })}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Container>
    </PageLayout>
  );
};

export default AiChatSidebarList;
